import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import CeroButton from "../../../../components/CeroButton";
import useStyles from "./styles";
import CeroInput from "../../../../components/CeroInput";
import AddIcon from "@mui/icons-material/Add";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { STATUS } from "../../../../redux/constants";
import CeroAutoComplete from "../../../../components/CeroAutoComplete";
import CeroPopUp from "../../../../components/CeroPopUp";
import ViewQuestions from "../ViewQuestions";
import { getMaterialityIndustries } from "../../../../redux/actions/materiality";
import { sendSupplierQuestions } from "../../../../redux/actions";
import clsx from "clsx";
import CloseIcon from "@mui/icons-material/Close";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import CeroTable from "../../../../components/CeroTable";

const UserDetails = [
  {
    columnKey: "name",
    columnId: "name",
    columnHeader: "Name",
    width: "15vw",
  },
  {
    columnKey: "email",
    columnId: "email",
    columnHeader: "Email",
    width: "18vw",
  },
];

const validationSchema = Yup.object().shape({
  users: Yup.array().when("$mode", {
    is: "manual",
    then: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is required"),
        email: Yup.string()
          .email("Invalid email format")
          .required("Email is required"),
        industry: Yup.string().nullable(),
      })
    ),
    otherwise: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().nullable(),
        email: Yup.string().nullable(),
        industry: Yup.string().nullable(),
      })
    ),
  }),
});

const SendQuestions = ({ setOpen, sendQuestionnairesStatus }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [fields, setFields] = useState([{ name: "", email: "", industry: "" }]);
  const [viewQuestionPopup, setViewQuestionPopup] = useState(false);
  const [currentIndustry, setCurrentIndustry] = useState("");

  const [data, setData] = useState([]);
  const [fileName, setFileName] = useState(null);

  const industryData =
    useSelector((state) => state.materiality.getMaterialityIndustries.data) ||
    [];

  const [mode, setMode] = useState("manual"); // "manual" | "bulk"

  const formik = useFormik({
    initialValues: {
      users: fields,
    },
    validationSchema: (values) =>
      Yup.object().shape({
        users: Yup.array().when([], {
          // no need for context
          is: () => mode === "manual",
          then: Yup.array().of(
            Yup.object().shape({
              name: Yup.string().required("Name is required"),
              email: Yup.string()
                .email("Invalid email format")
                .required("Email is required"),
              industry: Yup.string().nullable(),
            })
          ),
          otherwise: Yup.array().of(
            Yup.object().shape({
              name: Yup.string().nullable(),
              email: Yup.string().nullable(),
              industry: Yup.string().nullable(),
            })
          ),
        }),
      }),

    onSubmit: () => {
      let senderList = [];

      if (mode === "manual") {
        senderList = formik.values.users.map((u) => ({
          name: u.name,
          email: u.email,
          industry_type: u.industry,
        }));
      }

      if (mode === "bulk") {
        senderList = data.map((u) => ({
          name: u.name,
          email: u.email,
          industry_type: currentIndustry || formik.values.users[0]?.industry,
        }));
      }

      dispatch(
        sendSupplierQuestions({
          sender_list: senderList,
        })
      );

      formik.resetForm();
      setData([]); // clear bulk
      setOpen(false);
    },
  });

  const handleAddMore = () => {
    setFields([...fields, { name: "", email: "", industry: currentIndustry }]);
    formik.setFieldValue("users", [
      ...formik.values.users,
      { name: "", email: "", industry: currentIndustry },
    ]);
  };

  const canAddMore = formik.values.users.every(
    (item) => item.name !== "" && item.email !== ""
  );

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  useEffect(() => {
    dispatch(getMaterialityIndustries());
  }, [dispatch]);

  useEffect(() => {
    if (sendQuestionnairesStatus === STATUS.SUCCESS) {
      formik.resetForm();
    }
  }, [sendQuestionnairesStatus]);

  const processExcelData = (rawData) => {
    if (rawData.length < 2) return [];

    const headerRow = rawData[0];
    const dataRows = rawData.slice(1);

    const nameIndex = headerRow.findIndex(
      (h) => h && h.toLowerCase() === "name"
    );
    const emailIndex = headerRow.findIndex(
      (h) => h && h.toLowerCase() === "email"
    );

    if (nameIndex === -1 || emailIndex === -1) {
      return [];
    }

    const contacts = [];
    dataRows.forEach((row) => {
      const name = row[nameIndex];
      const email = row[emailIndex];

      if (name && email) {
        contacts.push({
          name: String(name).trim(),
          email: String(email).trim(),
        });
      }
    });

    return contacts;
  };

  const downloadTemplate = () => {
    const header = [["Name", "Email"]]; // first row = headers
    const ws = XLSX.utils.aoa_to_sheet(header);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    XLSX.writeFile(wb, "supplier_template.xlsx");
  };

  const onFileUpload = (file) => {
    console.log(file, "file");
  };
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMode("bulk");
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result;
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const processedData = processExcelData(json);
      setData(processedData); // <-- Table only
    };

    reader.readAsArrayBuffer(file);
  };

  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
  };

  const industryList = Array.isArray(industryData)
    ? industryData.map((item) => ({
        id: item.id,
        label: item.industry_type,
      }))
    : [];

  const handleViewQuestionnaireClick = (industryId) => {
    setViewQuestionPopup(true);
  };

  const handleIndustryChange = (value) => {
    const industryId = value?.id || "";
    setCurrentIndustry(industryId);

    const updatedUsers = formik.values.users.map((u) => ({
      ...u,
      industry: industryId,
    }));

    formik.setFieldValue("users", updatedUsers);
  };

  const [openQuestionsForm, setOpenQuestionsForm] = useState(false);
  const createQuestionStatus = useSelector(
    (state) => state.supplierScreening.createSupplierQuestions.status
  );
  const companyName = "company";

  return (
    <Box className={classes.popUp}>
      <CloseIcon onClick={handleCancel} className={classes.closeIcon} />
      <Typography variant="body1" className={classes.popupTitle}>
        Send Questionnaire to Suppliers
      </Typography>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <FieldArray name="users">
            {() => (
              <Grid
                display={"flex"}
                flexDirection="column"
                mt={4}
                gap={2}
                justifyContent="flex-start"
                className={classes.eachIndustryBox}
              >
                <Grid container spacing={2} display={"flex"}>
                  <Grid item xs={6} style={{ paddingTop: "8px" }}>
                    <CeroAutoComplete
                      classes={{
                        container: classes.autoComplete,
                        root: classes.root,
                        option: classes.autoCompleteOption,
                      }}
                      id={`industry-0`}
                      label="Supplier Industry"
                      fullWidth
                      value={
                        industryList.find((item) => item.id === currentIndustry)
                          ?.label || ""
                      }
                      onChange={(e, newValue) => handleIndustryChange(newValue)}
                      options={industryList || []}
                    />
                  </Grid>
                  <Grid item xs={6} style={{ paddingTop: "8px" }}>
                    <div className={classes.viewButton}>
                      <CeroButton
                        buttonText="View Questionnaire"
                        variant="contained"
                        className={clsx(classes.button, classes?.buttonPrimary)}
                        onClick={() => handleViewQuestionnaireClick()}
                        disabled={!currentIndustry}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Typography variant="body1" className={classes.choiceTitle}>
                  Add manually
                </Typography>
                <Typography
                  display={"flex"}
                  fontSize={14}
                  fontWeight={500}
                  variant="h3"
                >
                  Enter Name, Email ID & Selected Industry:
                </Typography>
                {formik.values.users.map((stakeholder, index) => (
                  <Box
                    key={index}
                    display={"flex"}
                    flexDirection="column"
                    gap={1}
                  >
                    <Grid container spacing={2} display={"flex"}>
                      <Grid
                        item
                        xs={6}
                        style={{ paddingTop: index !== 0 ? "0px" : "8px" }}
                      >
                        <CeroInput
                          required
                          id={`name-${index}`}
                          name={`users[${index}].name`}
                          label="Name"
                          value={formik.values.users[index].name}
                          onChange={(e) => {
                            // Do NOT switch to manual mode if bulk data exists
                            if (data.length === 0) {
                              setMode("manual");
                            }
                            formik.handleChange(e);
                          }}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.users &&
                            formik.touched.users[index]?.name &&
                            Boolean(
                              formik.errors.users &&
                                formik.errors.users[index]?.name
                            )
                          }
                          helperText={
                            formik.touched.users &&
                            formik.touched.users[index]?.name &&
                            formik.errors.users &&
                            formik.errors.users[index]?.name
                          }
                          classes={{
                            container:
                              index === 0
                                ? classes.input
                                : classes.inputWithoutMargin,
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        style={{ paddingTop: index !== 0 ? "0px" : "8px" }}
                      >
                        <CeroInput
                          required
                          id={`email-${index}`}
                          name={`users[${index}].email`}
                          label="Email"
                          value={formik.values.users[index].email}
                          onChange={(e) => {
                            if (data.length === 0) {
                              setMode("manual");
                            }
                            formik.handleChange(e);
                          }}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.users &&
                            formik.touched.users[index]?.email &&
                            Boolean(
                              formik.errors.users &&
                                formik.errors.users[index]?.email
                            )
                          }
                          helperText={
                            formik.touched.users &&
                            formik.touched.users[index]?.email &&
                            formik.errors.users &&
                            formik.errors.users[index]?.email
                          }
                          classes={{
                            container:
                              index === 0
                                ? classes.input
                                : classes.inputWithoutMargin,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Box
                  display="flex"
                  paddingY={2}
                  paddingLeft={2}
                  gap={1}
                  onClick={canAddMore ? handleAddMore : null}
                  style={{
                    cursor: canAddMore ? "pointer" : "not-allowed",
                    color: canAddMore ? "blue" : "gray",
                  }}
                >
                  <AddIcon />
                  <Typography>Add Name and Email</Typography>
                </Box>
                <Box className={classes.diffrenciater}>
                  <Box className={classes.line} />

                  <div className={classes.orDiv}>
                    <Typography className={classes.orText}>OR</Typography>{" "}
                  </div>

                  <Box className={classes.line} />
                </Box>

                <Box>
                  <Typography variant="body1" className={classes.choiceTitle}>
                    Upload file
                  </Typography>
                  <div className={classes.uploadButtonDiv}>
                    <Button
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      className={classes.buttonTeritory}
                      startIcon={<DownloadIcon />}
                      onClick={downloadTemplate}
                    >
                      Download template
                    </Button>

                    <label className={classes.uploadButton}>
                      <input
                        type="file"
                        className={classes.hideFile}
                        accept=".xlsx, .xls"
                        onChange={handleFileUpload}
                      />
                      Upload CSV/Excel File
                    </label>
                    {data.length > 0 && (
                      <div className={classes.tableDiv}>
                        <CeroTable
                          columns={UserDetails}
                          data={data}
                          hasMore={false}
                          loading={false}
                          onSelectRow={() => {
                            console.log();
                          }}
                          classes={{
                            tableContainer: classes.tableContainer,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Box>
              </Grid>
            )}
          </FieldArray>
          <div className={classes.buttonContainerReview}>
            <CeroButton
              className={classes.buttonPrimary}
              variant="contained"
              type="submit"
              buttonText="Send Email"
              onClick={handleSubmit}
              disabled={
                !(
                  (mode === "manual" &&
                    formik.values.users.every(
                      (user) => user.name && user.email && currentIndustry
                    )) ||
                  (mode === "bulk" && data.length > 0 && currentIndustry)
                )
              }
            />
            <CeroButton
              className={classes.buttonSecondary}
              variant="outlined"
              onClick={handleCancel}
              buttonText="Cancel"
            />
          </div>
        </form>
      </FormikProvider>
      <CeroPopUp
        primaryPopUp={{
          open: viewQuestionPopup,
          onClose: () => setViewQuestionPopup(false),
          content: ViewQuestions({
            openQuestionsForm,
            setOpenQuestionsForm,
            createQuestionStatus,
            companyName,
            currentIndustry,
          }),
          header: { title: "" },
        }}
        classes={{ dialog: classes.popUpdialog }}
        showCloseButton={true}
      />
    </Box>
  );
};

export default SendQuestions;
